import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  MenuItem,
  Typography,
  Box,
  Divider,
  Stack,
  Paper,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BugReportIcon from "@mui/icons-material/BugReport";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ImageIcon from "@mui/icons-material/Image";
import { getReadableRouteName } from "../../utils/imageUrl";
import { ROUTES } from "../../constants/routes";
import theme from "../../theme/theme";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";

const BugReportDialog = ({ open, onClose, onSubmit }) => {
  const location = useLocation();
  const currentPage = location.pathname;
  const readablePage = getReadableRouteName(ROUTES, location.pathname);


  const user = useSelector(selectUser);
  const userEmail = Array.isArray(user?.email) ? user.email[0] || "" : user?.email || "";

  const initialReport = {
    subject: "",
    severity: "",
    issue_description: "",
    firstName: "",
    lastName: "",
    email: userEmail,
    file: null,
  };

  const [report, setReport] = useState(initialReport);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Sync email if it loads later
  useEffect(() => {
    if (userEmail) {
      setReport((prev) => ({ ...prev, email: userEmail }));
    }
  }, [userEmail]);

  const handleChange = (key, value) => {
    setReport((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" })); // clear specific error
  };

  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (uploadedFile && uploadedFile.size <= 5 * 1024 * 1024) {
      handleChange("file", uploadedFile);
    } else {
      toast.error(
        "Unable to save your file. It exceeds the 5 MB limit. Please reduce the size and try again.",
        {
          autoClose: 5000,
          style: { backgroundColor: "#ffe4e4", color: "#b30000" },
        }
      );
    }
  };

  const handleRemoveFile = () => handleChange("file", null);

  // Validate mandatory fields
  const validateForm = () => {
    const newErrors = {};
    if (!report.subject.trim()) newErrors.subject = "Subject is required";
    if (!report.issue_description.trim())
      newErrors.issue_description = "Issue description is required";
    if (!report.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!report.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!report.email.trim()) newErrors.email = "Email is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(report).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      formData.append("page", currentPage);

      await onSubmit(formData);
      setReport(initialReport);
      onClose();
    } catch (error) {
      console.error("❌ Bug report submission failed:", error);
      toast.error("Something went wrong while submitting your report.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {/* Title */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <BugReportIcon sx={{ color: theme.palette.primary[600] }} />
          <Typography
            variant="h6"
            fontWeight="600"
            fontFamily="var(--m3-label-large-font-family)"
          >
            Submit a Bug Report
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ mt: 2 }}>
        {/* Subject */}
        <Box mb={2}>
          <Typography fontWeight="500" mb={0.5}>
            Subject{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <TextField
            fullWidth
            value={report.subject}
            onChange={(e) => handleChange("subject", e.target.value)}
            placeholder="Enter a short subject or title for this issue"
            error={!!errors.subject}
            helperText={errors.subject}
            disabled={submitting}
          />
        </Box>

        {/* Current Page */}
        <Box
          mb={2}
          sx={{
            backgroundColor: "#f9fafb",
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Current Page:
          </Typography>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              wordBreak: "break-all",
              fontFamily: "monospace",
              fontSize: "0.85rem",
            }}
          >
            {readablePage}
          </Typography>
        </Box>

        {/* Severity */}
        <Box mb={2}>
          <Typography fontWeight="500" mb={0.5}>
            Bug Severity
          </Typography>
          <TextField
            select
            fullWidth
            value={report.severity}
            onChange={(e) => handleChange("severity", e.target.value)}
            placeholder="Select severity"
            disabled={submitting}
          >
            <MenuItem value="Low">Low</MenuItem>
            <MenuItem value="Medium">Medium</MenuItem>
            <MenuItem value="High">High</MenuItem>
          </TextField>
        </Box>

        {/* Issue Description */}
        <Box mb={2}>
          <Typography fontWeight="500" mb={0.5}>
            Issue Description{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={report.issue_description}
            onChange={(e) => handleChange("issue_description", e.target.value)}
            placeholder="Describe the issue clearly..."
            error={!!errors.issue_description}
            helperText={errors.issue_description}
            disabled={submitting}
          />
        </Box>

        {/* User Info */}
        <Stack direction="row" spacing={2} mb={2}>
          <Box flex={1}>
            <Typography fontWeight="500" mb={0.5}>
              First Name{" "}
              <Typography component="span" color="error">
                *
              </Typography>
            </Typography>
            <TextField
              fullWidth
              value={report.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
              disabled={submitting}
              error={!!errors.firstName}
              helperText={errors.firstName}
            />
          </Box>
          <Box flex={1}>
            <Typography fontWeight="500" mb={0.5}>
              Last Name{" "}
              <Typography component="span" color="error">
                *
              </Typography>
            </Typography>
            <TextField
              fullWidth
              value={report.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
              disabled={submitting}
              error={!!errors.lastName}
              helperText={errors.lastName}
            />
          </Box>
        </Stack>

        {/* Email */}
        <Box mb={2}>
          <Typography fontWeight="500" mb={0.5}>
            Email Address{" "}
            <Typography component="span" color="error">
              *
            </Typography>
          </Typography>
          <TextField
            type="email"
            fullWidth
            value={report.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder="you@example.com"
            disabled={submitting}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Box>

        {/* File Upload */}
        <Box mb={3}>
          <Typography fontWeight="500" mb={0.5}>
            Attach Screenshot / File (5MB Max)
          </Typography>
          {!report.file ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: "30px",
                p: 1.2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Button
                variant="outlined"
                component="label"
                sx={{ borderRadius: "20px", textTransform: "none" }}
                disabled={submitting}
              >
                Choose File
                <input type="file" hidden onChange={handleFileChange} />
              </Button>
              <Typography variant="body2" color="text.secondary">
                No file chosen
              </Typography>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: "30px",
                p: 1.2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flex={1}
                minWidth={0}
              >
                <ImageIcon color="action" />
                <Typography
                  variant="body2"
                  sx={{
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    flex: 1,
                    maxWidth: "200px",
                  }}
                  title={report.file.name}
                >
                  {report.file.name}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleRemoveFile}
                disabled={submitting}
              >
                <DeleteOutlineIcon fontSize="small" color="error" />
              </IconButton>
            </Paper>
          )}
        </Box>
      </DialogContent>

      {/* Submit Button */}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={submitting}
          sx={{
            textTransform: "none",
            fontWeight: "600",
            py: 1,
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.nuetral[100],
            "&:hover": {
              backgroundColor: theme.palette.primary[800],
            },
          }}
        >
          {submitting ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              Submitting Report...
            </Box>
          ) : (
            "Submit Bug Report"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BugReportDialog;
